// Change #100450 - "ability to add adhoc overtime availability to allow the
// calendar to understand this and allow bookings".
//
// StaffWorkingScheduleAdjustment already existed and the schedule engine
// (StaffWorkingScheduleLibrary) already read it - FlagAdd adds a window for one
// date, FlagCancel takes the day out - but nothing on screen could create one.
// This is that screen. The slot search joins an added window onto the shift it
// touches, so overtime straight after the day can take a job that runs over.
//
// Change #100451 - the schedule's "Minimum rest" is checked on the server when
// extra time is saved; its message comes straight back as a toast.

import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, DeleteOutline } from "@mui/icons-material";
import { toast } from "react-toastify";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";

import {
  staffScheduleAdjustmentListApi,
  staffScheduleAdjustmentCreateApi,
  staffScheduleAdjustmentDeleteApi,
} from "../../../../helpers/fakebackend_helper";

const today = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const hhmm = (t) => String(t || "").slice(0, 5);

const formatDate = (value) => {
  const raw = String(value || "").slice(0, 10);
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d) return raw;
  const dt = new Date(Number(y), Number(m) - 1, Number(d));
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const emptyDraft = () => ({
  kind: "add",
  date: today(),
  startTime: "16:00",
  endTime: "18:00",
  notes: "",
});

// embedded = shown as its own tab on the Working Schedule screen (no outer
// border, and the tab already carries the title).
const ScheduleAdjustments = ({ staffId, scheduleId, minimumRestHours, embedded = false }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPast, setShowPast] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  const load = useCallback(async () => {
    if (!staffId) return;
    setLoading(true);
    try {
      const res = await staffScheduleAdjustmentListApi(
        staffId,
        showPast ? null : today()
      );
      setRows(res?.staffworkingscheduleadjustment_list || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [staffId, showPast]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    if (!draft.date) {
      toast.error("Pick a date.");
      return;
    }
    if (draft.kind === "add" && (!draft.startTime || !draft.endTime)) {
      toast.error("Extra time needs a start and an end time.");
      return;
    }
    if (draft.kind === "add" && draft.startTime === draft.endTime) {
      toast.error("The end time must be different from the start time.");
      return;
    }
    const row = {
      StaffWorkingSchedule_Id: Number(scheduleId),
      Date: draft.date,
      FlagAdd: draft.kind === "add" ? 1 : 0,
      FlagCancel: draft.kind === "off" ? 1 : 0,
      Notes: draft.notes || "",
    };
    if (draft.kind === "add") {
      row.StartTime = `${draft.startTime}:00`;
      row.EndTime = `${draft.endTime}:00`;
    }
    setSaving(true);
    try {
      const res = await staffScheduleAdjustmentCreateApi([row]);
      if (res?.status === 200) {
        toast.success(draft.kind === "add" ? "Extra time added." : "Day off added.");
        setOpen(false);
        setDraft(emptyDraft());
        load();
      } else {
        toast.error(res?.message || "Could not save.");
      }
    } catch (e) {
      toast.error("Could not save.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;
    try {
      const res = await staffScheduleAdjustmentDeleteApi([Number(deleteRow.Id)]);
      if (res?.status === 200) {
        toast.success("Removed.");
        load();
      } else {
        toast.error(res?.message || "Could not remove.");
      }
    } catch (e) {
      toast.error("Could not remove.");
    } finally {
      setDeleteRow(null);
    }
  };

  if (!scheduleId) return null;

  const restHours = Number(minimumRestHours) || 0;

  return (
    <Paper
      variant={embedded ? "elevation" : "outlined"}
      elevation={0}
      sx={embedded ? { p: 0, bgcolor: "transparent" } : { p: 2, mt: 2, borderRadius: 2 }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Box>
          {!embedded && (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Overtime and one-off changes
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {embedded
              ? ""
              : "Extra time counts as working time on that date, so the diary and field service dispatch can book into it."}
            {restHours > 0
              ? ` At least ${restHours} hours' rest is kept either side.`
              : ""}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showPast}
                onChange={(e) => setShowPast(e.target.checked)}
              />
            }
            label={<Typography variant="caption">Show past</Typography>}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setDraft(emptyDraft());
              setOpen(true);
            }}
          >
            Add
          </Button>
        </Stack>
      </Stack>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      ) : rows.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 5,
            border: "1px dashed",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Box sx={{ fontSize: 32, color: "#4f46e5", lineHeight: 1 }}>
            <i className="ri-time-line"></i>
          </Box>
          <Typography sx={{ fontWeight: 700, mt: 1 }}>
            {showPast ? "No one-off changes" : "No upcoming one-off changes"}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Add overtime when someone agrees to work extra, or a day off outside their leave.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Add />}
            onClick={() => {
              setDraft(emptyDraft());
              setOpen(true);
            }}
          >
            Add one-off change
          </Button>
        </Box>
      ) : (
        <Stack spacing={1}>
          {rows.map((r) => {
            const isAdd = String(r.FlagAdd) === "1";
            const isOff = String(r.FlagCancel) === "1";
            return (
              <Stack
                key={r.Id}
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                }}
              >
                <Chip
                  size="small"
                  color={isAdd ? "success" : isOff ? "warning" : "default"}
                  label={isAdd ? "Extra time" : isOff ? "Time off" : "Change"}
                />
                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 130 }}>
                  {formatDate(r.Date)}
                </Typography>
                <Typography variant="body2" sx={{ minWidth: 110 }}>
                  {r.StartTime && r.EndTime
                    ? `${hhmm(r.StartTime)} - ${hhmm(r.EndTime)}`
                    : "All day"}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1, minWidth: 0 }}
                  noWrap
                >
                  {r.Notes || ""}
                </Typography>
                <Tooltip title="Remove">
                  <IconButton size="small" color="error" onClick={() => setDeleteRow(r)}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
      )}

      {/* Change #100458 - the house AppModal / DeleteModal rather than raw MUI
          dialogs, so this looks like every other popup in the product. */}
      <AppModal
        open={open}
        onClose={() => !saving && setOpen(false)}
        title="One-off change"
        subtitle="Overtime or a day off on a single date"
        icon="ri-time-line"
        headerAccent="indigo"
        size="sm"
        onSubmit={save}
        submitLabel={draft.kind === "add" ? "Add extra time" : "Add day off"}
        submitDisabled={saving}
        loading={saving}
        loadingText="Saving..."
      >
        <Stack spacing={2}>
          <Grid container spacing={1.5}>
            {[
              {
                kind: "add",
                icon: "ri-add-circle-line",
                title: "Extra time",
                text: "Overtime they have agreed to work",
                color: "#059669",
              },
              {
                kind: "off",
                icon: "ri-calendar-close-line",
                title: "Day off",
                text: "Take the whole date out",
                color: "#d97706",
              },
            ].map((opt) => {
              const selected = draft.kind === opt.kind;
              return (
                <Grid item xs={6} key={opt.kind}>
                  <Box
                    role="button"
                    tabIndex={0}
                    onClick={() => setDraft((d) => ({ ...d, kind: opt.kind }))}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setDraft((d) => ({ ...d, kind: opt.kind }));
                      }
                    }}
                    sx={{
                      p: 1.5,
                      height: "100%",
                      borderRadius: 2,
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor: selected ? opt.color : "divider",
                      bgcolor: selected ? `${opt.color}14` : "background.paper",
                      transition: "all 0.15s",
                    }}
                  >
                    <Box sx={{ fontSize: 22, color: opt.color, lineHeight: 1 }}>
                      <i className={opt.icon}></i>
                    </Box>
                    <Typography sx={{ fontWeight: 700, mt: 0.5 }}>{opt.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {opt.text}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          <TextField
            type="date"
            label="Date"
            size="small"
            value={draft.date}
            onChange={(e) => setDraft((d) => ({ ...d, date: e.target.value }))}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />

          {draft.kind === "add" ? (
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  type="time"
                  label="From"
                  size="small"
                  value={draft.startTime}
                  onChange={(e) => setDraft((d) => ({ ...d, startTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="time"
                  label="To"
                  size="small"
                  value={draft.endTime}
                  onChange={(e) => setDraft((d) => ({ ...d, endTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  helperText={
                    draft.endTime && draft.startTime && draft.endTime < draft.startTime
                      ? "Runs past midnight"
                      : undefined
                  }
                />
              </Grid>
            </Grid>
          ) : (
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: "rgba(217, 119, 6, 0.08)",
                color: "#92400e",
                fontSize: 13,
              }}
            >
              <i className="ri-information-line me-1"></i>
              For holidays and sickness use leave instead, so it counts against the allowance.
            </Box>
          )}

          {draft.kind === "add" && Number(minimumRestHours) > 0 && (
            <Typography variant="caption" color="text.secondary">
              <i className="ri-zzz-line me-1"></i>
              At least {Number(minimumRestHours)} hours&apos; rest must be left before and after.
            </Typography>
          )}

          <TextField
            label="Notes"
            size="small"
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            placeholder="e.g. agreed for the Smith job"
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </AppModal>

      <DeleteModal
        show={Boolean(deleteRow)}
        onCloseClick={() => setDeleteRow(null)}
        onDeleteClick={confirmDelete}
        customText={
          deleteRow
            ? `Remove the ${String(deleteRow.FlagAdd) === "1" ? "extra time" : "day off"} on ${formatDate(deleteRow.Date)}${
                deleteRow.StartTime ? `, ${hhmm(deleteRow.StartTime)} - ${hhmm(deleteRow.EndTime)}` : ""
              }?`
            : ""
        }
        customButtonText="Remove"
      />
    </Paper>
  );
};

export default ScheduleAdjustments;
